import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import fallbackPortfolioDB from "../../../data/portfolioDB.json";

const ProfileContext = createContext();

const apiBase = process.env.REACT_APP_BACKEND_API_URL;

const DEFAULT_HERO_STATS = [
  { icon: "rocket", value: "1+", label: "Years Experience" },
  { icon: "folder", value: "10+", label: "Projects Completed" },
  { icon: "code", value: "50K+", label: "Lines of Code" },
  { icon: "star", value: "5★", label: "Problem Solving" },
];

function normalizeProfile(payload) {
  if (!payload) return null;
  const fb = fallbackPortfolioDB;
  const expDetails = Array.isArray(payload.expDetails) ? payload.expDetails : [];
  const eduDetails = Array.isArray(payload.eduDetails) ? payload.eduDetails : [];
  const raw = payload.intro && typeof payload.intro === "object" ? payload.intro : null;
  const fbIntro = fb.intro || {};
  const rotatingTitles =
    Array.isArray(raw?.rotatingTitles) && raw.rotatingTitles.length
      ? raw.rotatingTitles
      : fbIntro.rotatingTitles ?? [];
  const heroStatsRaw = Array.isArray(raw?.heroStats) && raw.heroStats.length ? raw.heroStats : fbIntro.heroStats;
  const heroStats =
    Array.isArray(heroStatsRaw) && heroStatsRaw.length ? heroStatsRaw : DEFAULT_HERO_STATS;
  const heroBadgeDefault =
    typeof fbIntro.heroBadge === "string" && fbIntro.heroBadge.trim()
      ? fbIntro.heroBadge.trim()
      : rotatingTitles[0]
        ? String(rotatingTitles[0]).toUpperCase()
        : "FULL STACK DEVELOPER";
  return {
    expDetails,
    eduDetails,
    intro: {
      greeting: raw?.greeting ?? fbIntro.greeting ?? "",
      rotatingTitles,
      bio: raw?.bio ?? fbIntro.bio ?? "",
      imageSrc: raw?.imageSrc ?? fbIntro.imageSrc ?? "images/me.jpg",
      heroBadge: raw?.heroBadge?.trim() || heroBadgeDefault,
      heroStats,
    },
  };
}

function ProfileProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadRequested = useRef(false);

  const loadProfile = useCallback(async () => {
    if (!apiBase) {
      setProfile(normalizeProfile(fallbackPortfolioDB));
      setLoading(false);
      setError(new Error("REACT_APP_BACKEND_API_URL is not set"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/profile`, {
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      const json = await res.json();
      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Failed to load profile");
      }
      const data = json.data != null ? json.data : json;
      setProfile(normalizeProfile(data));
    } catch (e) {
      console.warn("Profile API failed, using static fallback:", e.message);
      setError(e);
      setProfile(normalizeProfile(fallbackPortfolioDB));
    } finally {
      setLoading(false);
    }
  }, []);

  const requestLoad = useCallback(() => {
    if (loadRequested.current) return;
    loadRequested.current = true;
    void loadProfile();
  }, [loadProfile]);

  const value = useMemo(() => {
    const normalized = profile ?? normalizeProfile(fallbackPortfolioDB);
    return {
      expDetails: normalized?.expDetails ?? [],
      eduDetails: normalized?.eduDetails ?? [],
      intro: normalized?.intro,
      loading,
      error,
      refetch: loadProfile,
      requestLoad,
    };
  }, [profile, loading, error, loadProfile, requestLoad]);

  return (
    <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
  );
}

function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  useEffect(() => {
    ctx.requestLoad();
  }, [ctx.requestLoad]);
  return ctx;
}

export { ProfileProvider, useProfile };
